import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { styled } from "@mui/material";
import dayjs from "dayjs";

import {
  CustomButton,
  ClientStatusBadge,
  Text,
} from "../../../shared/uiComponents";
import { useCheckPermission } from "../../../shared/Helpers/hooks";
import ReinforcementItems from "./ReinforcementItems";
import { PERMISSIONS } from "../../../App/constants";

import { DispatchProperties, useSelector } from "../../../redux/store";
import { clearActiveAuthorization } from "../../../redux/State/clientSlice/insuranceAuthorizationSlice";
import {
  clearClients,
  clearTherapists,
} from "../../../redux/State/clientSlice/clientSlice";
import { getReinforcements } from "../../../redux/State/clientSlice/programTargetSlice";

const Wrapper = styled("div")(() => ({
  maxWidth: "360px",
  paddingRight: "16px",
  paddingBottom: "16px",
}));

const ClientInfo = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const ALLOCATE = useCheckPermission(PERMISSIONS.INSURANCE_AUTHORIZATION.ALLOCATE);
  const ASSIGN_BCBA = useCheckPermission(PERMISSIONS.SOAPNOTE.ASSIGN_BCBA);
  const ASSIGN_BT = useCheckPermission(PERMISSIONS.SOAPNOTE.ASSIGN_RBT);

  const isAssignAllowed = ASSIGN_BCBA.permissionGranted || ASSIGN_BT.permissionGranted;

  const {
    id,
    fullName,
    dateOfBirthday,
    address,
    insurances,
    status,
    caseCoordinator,
  } = useSelector((state) => state.client.client);
  const reinforcements = useSelector(
    (state) => state.programTarget.reinforcementsList
  );

  const navigateAssignTherapistHandler = () => {
    if (!id) return;
    navigate(`/clients/client/assign/${id}`);
    dispatch(clearActiveAuthorization());
    dispatch(clearTherapists());
    dispatch(clearClients());
  };

  const navigateAllocateHours = () => {
    dispatch(clearActiveAuthorization());
    navigate(`/clients/client/allocate-authorization/${id}`);
  };

  useEffect(() => {
    if (!clientId) return;
    dispatch(getReinforcements({ clientId }));
  }, [clientId, dispatch]);

  return (
    <Wrapper>
      <Text title={fullName} size="largeBold" className="marginBottom8" />
      <Text
        title={`DOB: ${dayjs(dateOfBirthday).format("MM-DD-YYYY")}`}
        className="marginBottom8"
      />
      <Text
        title={`Address: ${!!address ? address : "-"}`}
        className="marginBottom8"
      />
      {!!insurances?.length &&
        insurances.map(({ name, insuranceNumber, type }) => (
          <div className="marginBottom8">
            <Text
              title={`${type === 1 ? "Primary" : "Secondary"} Insurance: ${!!name ? name : "-"
                }`}
            />
            <Text
              title={`Member ID: ${!!insuranceNumber ? insuranceNumber : "-"}`}
            />
          </div>
        ))}
      {
        caseCoordinator &&
        <Text
          title={`Case Coordinator: ${caseCoordinator.fullName}`}
          className="marginBottom8"
        />
      }
      <div
        style={
          !reinforcements.length
            ? { display: "flex", justifyContent: "space-between" }
            : {}
        }
      >
        <Text
          title={`Reinforcement Items: `}
          size="smallBold"
          className="marginBottom8"
        />
        <ReinforcementItems />
      </div>

      <div className="marginLeft16 marginBottom16">
        <ClientStatusBadge statusCode={status.id} />
      </div>

      {isAssignAllowed && (
        <CustomButton
          title="Assign Provider"
          onClick={navigateAssignTherapistHandler}
          className="marginTop16"
          disabled={!id}
        />
      )}
      {ALLOCATE.permissionGranted && (
        <CustomButton
          title="Allocate hours"
          secondaryButton
          onClick={navigateAllocateHours}
          className="marginTop16"
          disabled={!id}
        />
      )}
    </Wrapper>
  );
};

export default ClientInfo;
